import { DateTime, Duration, DurationObjectUnits } from "luxon";
import { v4 as uuidv4 } from "uuid";
import { LOG_TO_CONSOLE } from "./FeatureFlags";

type FormValueTarget = {
    type?: string;
    value?: unknown;
    checked?: boolean;
};
type LinkStateValue = {
    setIn: (path: string, value: unknown) => unknown;
};
type LinkStateComponent = {
    __linkStateHandlers?: Record<
        string,
        (event: { target: FormValueTarget }) => void
    >;
    state?: Record<string, LinkStateValue | unknown>;
    setState?: (state: unknown) => void;
};

const emailCheck =
    // tslint:disable-next-line:max-line-length
    /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

export default class ViridisUtils {
    public static getUuid(): string {
        return uuidv4();
    }
    public static generateChemName(): string {
        return `CHEM_${this.getUuid().substring(0, 5)}`;
    }
    public static linkState(
        component: LinkStateComponent,
        key: string,
        path?: string
    ) {
        if (path) {
            return this.createHandler(component, key, path);
        }

        const cache =
            component.__linkStateHandlers ||
            (component.__linkStateHandlers = {});

        return cache[key] || (cache[key] = this.createHandler(component, key));
    }

    public static getParameterByName(
        name: string,
        path: string = location.search
    ): string | null {
        name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
        const regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
        const results = regex.exec(path);
        return results === null
            ? null
            : decodeURIComponent(results[1].replace(/\+/g, " "));
    }

    public static resolveAssetPath(path: string): string {
        return `//assets-viridischem-com.s3.amazonaws.com/static/${path}`;
    }
    public static isString(s: unknown): boolean {
        return typeof s === "string" || s instanceof String;
    }
    public static isInteger(n: unknown): boolean {
        return Number.isInteger(n);
    }
    public static isNumeric(n: unknown): boolean {
        if (typeof n !== "string" && typeof n !== "number") {
            return false;
        }
        return !isNaN(parseFloat(String(n))) && isFinite(Number(n));
    }
    public static isDecimalNumber(n: unknown): boolean {
        const re = /^\d+(\.\d{1,2})?$/;
        // if value is not blank, then test the regex
        return typeof n === "string" && n !== "" && re.test(n);
    }
    public static roundNumber(numberInput: unknown, decimals: number) {
        if (!this.isNumeric(numberInput)) {
            return numberInput;
        }

        const num: number = Number(numberInput);

        const roundedNum = Number(
            Math.round(Number(num + "e" + decimals)) + "e-" + decimals
        );

        if (isNaN(roundedNum)) {
            return num;
        } else {
            return roundedNum;
        }
    }
    public static validateEmail(email: string): boolean {
        return emailCheck.test(email);
    }
    public static showCitations() {
        return false;
    }
    public static log(message: unknown) {
        if (LOG_TO_CONSOLE()) {
            // tslint:disable-next-line no-console
            console.log(message);
        }
    }
    public static logObject(message: string, object: unknown) {
        if (LOG_TO_CONSOLE()) {
            // tslint:disable-next-line no-console
            console.log(`${message}: ${JSON.stringify(object)}`);
        }
    }

    public static humanReadableEpochSecond(
        epochSeconds: number,
        reverse: boolean = false
    ) {
        if (!epochSeconds) {
            return "";
        }

        const dt = DateTime.fromSeconds(epochSeconds);
        const formattedDt = dt.toLocaleString(DateTime.DATETIME_MED);

        if (reverse) {
            return `${formattedDt} (${this.toHumanDuration(
                dt,
                DateTime.now()
            )} ago)`;
        } else {
            return `${formattedDt} (in ${this.toHumanDuration(
                DateTime.now(),
                dt
            )})`;
        }
    }

    private static toHumanDuration(start: DateTime, end: DateTime): string {
        // Better Duration.toHuman support https://github.com/moment/luxon/issues/1134
        const duration = end
            .diff(start)
            .shiftTo("years", "days", "hours", "minutes", "seconds")
            .toObject();

        if ("seconds" in duration) {
            duration.seconds = Math.round(duration.seconds);
        }

        const cleanedDuration = Object.fromEntries(
            Object.entries(duration).filter(([, v]) => v !== 0)
        ) as DurationObjectUnits;

        if (Object.keys(cleanedDuration).length === 0) {
            cleanedDuration.seconds = 0;
        }

        return Duration.fromObject(cleanedDuration).toHuman();
    }

    public static copyToClipboard(data: unknown): void {
        const text =
            typeof data === "string" ? data : JSON.stringify(data ?? "");
        navigator.clipboard.writeText(text);
    }

    private static createHandler(
        component: LinkStateComponent,
        key: string,
        path?: string
    ) {
        return (e: { target: FormValueTarget }) => {
            const el = e.target;
            const value = el.type === "checkbox" ? el.checked : el.value;
            const currentValue = component.state?.[key];
            component.setState?.({
                [key]:
                    path &&
                    currentValue &&
                    typeof currentValue === "object" &&
                    "setIn" in currentValue
                        ? (currentValue as LinkStateValue).setIn(path, value)
                        : value,
            });
        };
    }
}
